import React, { useState, useEffect } from 'react';
import { useWalletStore } from '../store/walletStore';
import * as api from '../api/vexforge';
import { signAndSend, ensureConnected } from '../lib/submit';
import toast from 'react-hot-toast';
import {
  CommandLineIcon,
  ShieldCheckIcon,
  PlayIcon,
  FlagIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  DocumentTextIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import VipLibrary from './VipLibrary';
import VipAssistant from './VipAssistant';

const TABS = [
  { id: 'library', name: 'Library', icon: RectangleStackIcon },
  { id: 'assistant', name: 'AI Builder', icon: CommandLineIcon },
  { id: 'browse', name: 'Browse VIPs', icon: MagnifyingGlassIcon },
  { id: 'register', name: 'Register', icon: PlusIcon },
  { id: 'simulate', name: 'Simulate', icon: PlayIcon },
  { id: 'anchor', name: 'Anchor Data', icon: DocumentTextIcon },
];

function SafetyBadge({ level, score }) {
  const colors = {
    Verified: 'bg-green-500/20 text-green-400 border-green-500/30',
    Standard: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Warning: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Blocked: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colors[level] || colors.Standard}`}>
      <ShieldCheckIcon className="w-3 h-3 mr-1" />
      {level} ({score}/100)
    </span>
  );
}

function ProgramCard({ program, onSelect }) {
  return (
    <div
      onClick={() => onSelect(program)}
      className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-5 hover:border-vextaris-purple/50 cursor-pointer transition-all hover:shadow-lg hover:shadow-vextaris-purple/10"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-white font-semibold text-lg">
            <span className="text-gray-400">{program.namespace}/</span>
            {program.name}
          </h3>
          <span className="text-xs text-gray-500">v{program.version}</span>
        </div>
        <SafetyBadge level={program.safety_level} score={program.safety_score} />
      </div>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{program.description}</p>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{program.steps_count} steps / {program.params_count} params</span>
        <span>{program.invocation_count?.toLocaleString() || 0} invocations</span>
      </div>
    </div>
  );
}

function BrowseTab() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nsFilter, setNsFilter] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const results = await api.listPrograms(nsFilter || null, null, 50);
      setPrograms(results);
    } catch (err) {
      // Empty list is fine — no VIPs registered yet
      setPrograms([]);
    }
    setLoading(false);
  };

  const loadProgramDetail = async (prog) => {
    try {
      const detail = await api.getProgram(prog.namespace, prog.name);
      setSelectedProgram(detail);
    } catch (err) {
      toast.error('Failed to load VIP details');
    }
  };

  if (selectedProgram) {
    return (
      <div>
        <button
          onClick={() => setSelectedProgram(null)}
          className="text-vextaris-purple hover:text-white text-sm mb-4 flex items-center"
        >
          &larr; Back to list
        </button>
        <ProgramDetail program={selectedProgram} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center space-x-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            type="text"
            placeholder="Filter by namespace..."
            value={nsFilter}
            onChange={(e) => setNsFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
          />
        </div>
        <button
          onClick={loadPrograms}
          className="px-4 py-2 bg-vextaris-purple hover:bg-vextaris-purple/80 text-white rounded-lg text-sm font-medium transition-colors"
        >
          Search
        </button>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading VIPs...</div>
      ) : programs.length === 0 ? (
        <div className="text-center py-16">
          <CommandLineIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No VIPs registered yet</h3>
          <p className="text-gray-400 mb-6">Be the first to register a Vextaris Intent Program.</p>
          <p className="text-gray-500 text-sm">Switch to the <span className="text-vextaris-purple">Register</span> tab to create one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {programs.map((p, i) => (
            <ProgramCard key={i} program={p} onSelect={loadProgramDetail} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProgramDetail({ program }) {
  const { address } = useWalletStore();
  const [flagReason, setFlagReason] = useState('');
  const [showFlag, setShowFlag] = useState(false);

  const handleFlag = async () => {
    if (!flagReason.trim()) return toast.error('Provide a reason');
    if (!address) { await ensureConnected(); return; }
    try {
      const txHash = await signAndSend(
        api.flagProgramOps(program.namespace, program.name, flagReason.trim()),
        `Flag VIP ${program.namespace}/${program.name} for review`,
      );
      if (!txHash) return;
      toast.success('VIP flag submitted for review');
      setShowFlag(false);
      setFlagReason('');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-white">
              <span className="text-gray-400">{program.namespace}/</span>{program.name}
            </h2>
            <p className="text-gray-400 mt-1">v{program.version} by {program.author}</p>
          </div>
          <div className="flex items-center space-x-3">
            <SafetyBadge level={program.safety_level} score={program.safety_score} />
            <button
              onClick={() => setShowFlag(!showFlag)}
              className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
              title="Flag for review"
            >
              <FlagIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p className="text-gray-300 mt-3">{program.description}</p>
        <div className="flex space-x-6 mt-4 text-sm text-gray-500">
          <span>Registered at block {program.registered_at}</span>
          <span>{program.invocation_count} invocations</span>
          <span>{program.flag_count} flags</span>
        </div>
      </div>

      {/* Flag form */}
      {showFlag && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <h4 className="text-red-400 font-medium mb-2">Flag this VIP</h4>
          <textarea
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            placeholder="Describe why this VIP should be reviewed..."
            className="w-full px-4 py-2 bg-vextaris-darker border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 h-20 resize-none"
          />
          <button
            onClick={handleFlag}
            className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
          >
            Submit Flag
          </button>
        </div>
      )}

      {/* Parameters */}
      {program.params?.length > 0 && (
        <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Parameters</h3>
          <div className="space-y-2">
            {program.params.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                <div>
                  <span className="text-vextaris-purple font-mono text-sm">{p.name}</span>
                  <span className="text-gray-500 text-xs ml-2">({p.type})</span>
                  {p.required && <span className="text-red-400 text-xs ml-2">required</span>}
                </div>
                <span className="text-gray-400 text-sm">{p.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Steps */}
      {program.steps?.length > 0 && (
        <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Execution Steps</h3>
          <div className="space-y-3">
            {program.steps.map((s, i) => (
              <div key={i} className="flex items-start space-x-3 py-2">
                <span className="text-vextaris-purple font-mono text-sm w-6 text-right flex-shrink-0">{i + 1}.</span>
                <div>
                  <span className="text-white font-medium">{s.operation}</span>
                  <span className="text-gray-500 text-xs ml-2">[{s.id}]</span>
                  {s.loop_over && (
                    <span className="text-yellow-400 text-xs ml-2">loop: {s.loop_over}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RegisterTab() {
  const { address, isConnected } = useWalletStore();
  const [json, setJson] = useState('');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const template = JSON.stringify({
    namespace: "myapp",
    name: "my-program",
    version: 1,
    author: address || "Vx0YourAddress",
    description: "Describe what this VIP does",
    params: [
      { name: "recipient", type: "Address", required: true, default_value: null, description: "Who receives the tokens" },
      { name: "amount", type: "Amount", required: true, default_value: null, description: "How much to send" }
    ],
    steps: [
      {
        id: "transfer",
        operation: "Transfer",
        args: [["to", "${params.recipient}"], ["amount", "${params.amount}"]],
        loop_over: null
      }
    ],
    returns: [["tx", "${steps.transfer.result.tx_hash}"]]
  }, null, 2);

  const handleRegister = async () => {
    if (!json.trim()) return toast.error('Enter VIP JSON');
    if (!isConnected) { await ensureConnected(); return; }

    // Validate JSON
    try {
      const parsed = JSON.parse(json);
      if (!parsed.namespace || !parsed.name) throw new Error('Missing namespace or name');
      // Set author to connected wallet
      parsed.author = address;
      setSubmitting(true);
      const txHash = await signAndSend(
        api.registerProgramOps(JSON.stringify(parsed)),
        `Register VIP ${parsed.namespace}/${parsed.name}`,
      );
      if (txHash) {
        setResult({ success: true, txHash, namespace: parsed.namespace, name: parsed.name });
        toast.success('VIP registration submitted!');
      }
    } catch (err) {
      toast.error(err.message);
      setResult({ success: false, error: err.message });
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold">VIP Program Definition (JSON)</h3>
          <button
            onClick={() => setJson(template)}
            className="text-xs text-vextaris-purple hover:text-white transition-colors"
          >
            Load Template
          </button>
        </div>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          placeholder="Paste your VIP JSON here..."
          className="w-full h-96 px-4 py-3 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-green-400 font-mono text-sm placeholder-gray-600 focus:outline-none focus:border-vextaris-purple resize-none"
          spellCheck={false}
        />

        <div className="flex justify-between items-center mt-4">
          <p className="text-gray-500 text-xs">
            Registration fee: 50 VXS. Safety analysis runs automatically.
          </p>
          <button
            onClick={handleRegister}
            disabled={submitting}
            className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {submitting ? 'Registering...' : 'Register VIP'}
          </button>
        </div>
      </div>

      {result && (
        <div className={`border rounded-xl p-6 ${result.success ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          {result.success ? (
            <>
              <h4 className="text-green-400 font-semibold mb-2">VIP Registered Successfully</h4>
              <p className="text-gray-300 text-sm">
                <span className="text-white">{result.namespace}/{result.name}</span> is now live on Vextaris.
              </p>
              <p className="text-gray-500 text-xs mt-2">Bundle hash: {result.txHash}</p>
            </>
          ) : (
            <>
              <h4 className="text-red-400 font-semibold mb-2">Registration Failed</h4>
              <p className="text-gray-300 text-sm">{result.error}</p>
            </>
          )}
        </div>
      )}

      {/* Quick Reference */}
      <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-3">Quick Reference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="text-vextaris-purple font-medium mb-2">Template Expressions</h4>
            <div className="space-y-1 text-gray-400 font-mono text-xs">
              <p><span className="text-green-400">{"${sender}"}</span> - invoker's address</p>
              <p><span className="text-green-400">{"${params.name}"}</span> - user parameter</p>
              <p><span className="text-green-400">{"${steps.id.result.field}"}</span> - prior step output</p>
              <p><span className="text-green-400">{"${item}"}</span> - loop iteration value</p>
              <p><span className="text-green-400">{"${block.height}"}</span> - current block</p>
            </div>
          </div>
          <div>
            <h4 className="text-vextaris-purple font-medium mb-2">Parameter Types</h4>
            <div className="space-y-1 text-gray-400 font-mono text-xs">
              <p>String, Address, Amount, U8, U64, Bool</p>
              <p>AddressArray, U64Array, StringArray</p>
            </div>
            <h4 className="text-vextaris-purple font-medium mt-3 mb-2">Safety Rules</h4>
            <div className="text-gray-400 text-xs">
              <p>No hardcoded recipients. No hidden approvals.</p>
              <p>Max 50 operations. Allowed ops only.</p>
            </div>
          </div>
        </div>
        <p className="text-gray-500 text-xs mt-4">
          Full docs: <a href="https://docs.vextaris.io/developers/vip-guide" className="text-vextaris-purple hover:text-white">docs.vextaris.io/developers/vip-guide</a>
        </p>
      </div>
    </div>
  );
}

function SimulateTab() {
  const [namespace, setNamespace] = useState('');
  const [name, setName] = useState('');
  const [paramsJson, setParamsJson] = useState('[]');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSimulate = async () => {
    if (!namespace || !name) return toast.error('Enter namespace and name');
    setLoading(true);
    try {
      const sim = await api.simulateProgram(namespace, name, paramsJson);
      setResult(sim);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4">Simulate VIP Execution</h3>
        <p className="text-gray-400 text-sm mb-4">
          Dry-run a VIP to see what operations will execute. No gas is spent. Always simulate before invoking.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Namespace</label>
            <input
              type="text"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              placeholder="e.g. vexalus"
              className="w-full px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Program Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. create_tracked_shipment"
              className="w-full px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-1">Parameters (JSON array of [key, value] pairs)</label>
          <textarea
            value={paramsJson}
            onChange={(e) => setParamsJson(e.target.value)}
            placeholder='[["recipient","Vx0..."],["amount","1000000000"]]'
            className="w-full h-24 px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-green-400 font-mono text-sm placeholder-gray-600 focus:outline-none focus:border-vextaris-purple resize-none"
            spellCheck={false}
          />
        </div>
        <button
          onClick={handleSimulate}
          disabled={loading}
          className="px-6 py-2 bg-vextaris-blue hover:bg-vextaris-blue/80 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {loading ? 'Simulating...' : 'Simulate'}
        </button>
      </div>

      {result && (
        <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-semibold">Simulation Result</h3>
            <SafetyBadge level={result.safety_level} score={result.safety_score} />
          </div>
          <div className="mb-4 text-sm text-gray-400">
            <span className="text-white">{result.program}</span> &mdash;
            {result.total_operations} operations, ~{result.total_estimated_gas?.toLocaleString()} gas
          </div>
          <div className="space-y-2">
            {result.expanded_steps?.map((step, i) => (
              <div key={i} className="flex items-center space-x-3 py-2 px-3 bg-vextaris-dark rounded-lg">
                <span className="text-vextaris-purple font-mono text-sm w-6 text-right">{i + 1}.</span>
                <span className="text-white font-medium">{step.operation}</span>
                <span className="text-gray-500 text-xs">[{step.step_id}]</span>
                <span className="ml-auto text-gray-500 text-xs">{step.estimated_gas?.toLocaleString()} gas</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnchorTab() {
  const { isConnected } = useWalletStore();
  const [mode, setMode] = useState('create'); // 'create' | 'query' | 'verify'
  const [namespace, setNamespace] = useState('');
  const [keyHex, setKeyHex] = useState('');
  const [valueHash, setValueHash] = useState('');
  const [metadataUri, setMetadataUri] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnchor = async () => {
    if (!namespace || !keyHex || !valueHash) return toast.error('Fill all required fields');
    if (!isConnected) { await ensureConnected(); return; }
    setLoading(true);
    try {
      const keyBytesHex = '0x' + String(keyHex).trim().replace(/^0x/, '');
      if (!/^0x([0-9a-fA-F]{2})+$/.test(keyBytesHex)) throw new Error('Key must be hex bytes');
      const txHash = await signAndSend(
        api.anchorOps(namespace, keyBytesHex.toLowerCase(), valueHash, metadataUri || null),
        `Anchor data in "${namespace}"`,
      );
      if (txHash) {
        toast.success('Anchor submitted!');
        setResults({ type: 'anchored', txHash });
      }
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  const handleQuery = async () => {
    if (!namespace || !keyHex) return toast.error('Enter namespace and key');
    setLoading(true);
    try {
      const anchors = await api.getAnchors(namespace, keyHex);
      setResults({ type: 'query', anchors });
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!namespace || !keyHex || !valueHash) return toast.error('Fill all fields');
    setLoading(true);
    try {
      const verification = await api.verifyAnchor(namespace, keyHex, valueHash);
      setResults({ type: 'verify', verification });
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Mode selector */}
      <div className="flex space-x-2">
        {[
          { id: 'create', label: 'Anchor Data' },
          { id: 'query', label: 'Query Anchors' },
          { id: 'verify', label: 'Verify Hash' },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => { setMode(m.id); setResults(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === m.id
                ? 'bg-vextaris-purple text-white'
                : 'bg-vextaris-darker text-gray-400 hover:text-white border border-vextaris-purple/20'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Namespace</label>
            <input
              type="text"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              placeholder="e.g. vexalus, myapp"
              className="w-full px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Key (hex)</label>
            <input
              type="text"
              value={keyHex}
              onChange={(e) => setKeyHex(e.target.value)}
              placeholder="e.g. 696e766f6963652d303031"
              className="w-full px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
        </div>

        {(mode === 'create' || mode === 'verify') && (
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Value Hash (Vxh... or 0x...)</label>
              <input
                type="text"
                value={valueHash}
                onChange={(e) => setValueHash(e.target.value)}
                placeholder="VxhABC123... or 0x..."
                className="w-full px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            {mode === 'create' && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Metadata URI (optional)</label>
                <input
                  type="text"
                  value={metadataUri}
                  onChange={(e) => setMetadataUri(e.target.value)}
                  placeholder="ipfs://Qm... or https://..."
                  className="w-full px-4 py-2 bg-vextaris-dark border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
                />
              </div>
            )}
          </div>
        )}

        <button
          onClick={mode === 'create' ? handleAnchor : mode === 'query' ? handleQuery : handleVerify}
          disabled={loading}
          className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {loading ? 'Processing...' : mode === 'create' ? 'Anchor (5,000 gas)' : mode === 'query' ? 'Query' : 'Verify'}
        </button>
      </div>

      {/* Results */}
      {results?.type === 'anchored' && (
        <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6">
          <h4 className="text-green-400 font-semibold mb-2">Data Anchored</h4>
          <p className="text-gray-500 text-xs">Bundle hash: {results.txHash}</p>
        </div>
      )}

      {results?.type === 'query' && (
        <div className="bg-vextaris-darker border border-vextaris-purple/20 rounded-xl p-6">
          <h4 className="text-white font-semibold mb-4">{results.anchors?.length || 0} Anchor(s) Found</h4>
          {results.anchors?.length === 0 ? (
            <p className="text-gray-400">No anchors found for this namespace:key.</p>
          ) : (
            <div className="space-y-3">
              {results.anchors.map((a, i) => (
                <div key={i} className="bg-vextaris-dark rounded-lg p-3">
                  <div className="text-sm text-white font-mono break-all">{a.value_hash}</div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>by {a.author}</span>
                    <span>block {a.block_height}</span>
                  </div>
                  {a.metadata_uri && <div className="text-xs text-vextaris-purple mt-1">{a.metadata_uri}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {results?.type === 'verify' && (
        <div className={`border rounded-xl p-6 ${results.verification?.verified ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          <h4 className={`font-semibold mb-2 ${results.verification?.verified ? 'text-green-400' : 'text-red-400'}`}>
            {results.verification?.verified ? 'Verified' : 'Not Found'}
          </h4>
          {results.verification?.match && (
            <div className="text-gray-400 text-sm">
              <p>Author: {results.verification.match.author}</p>
              <p>Block: {results.verification.match.block_height}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VipStudio() {
  const [activeTab, setActiveTab] = useState('library');

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Intent Studio</h1>
        <p className="text-gray-400 mt-2">
          Build, register, simulate, and invoke VIPs (Vextaris Intent Programs) &mdash; declarative programs that compose native operations without smart contracts.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-8 bg-vextaris-darker rounded-xl p-1 border border-vextaris-purple/20">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 justify-center ${
                activeTab === tab.id
                  ? 'bg-vextaris-purple text-white shadow-lg shadow-vextaris-purple/20'
                  : 'text-gray-400 hover:text-white hover:bg-vextaris-purple/10'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'library' && <VipLibrary />}
      {activeTab === 'assistant' && (
        <VipAssistant onVipGenerated={(json) => {
          // Switch to Register tab with pre-filled JSON
          setActiveTab('register');
          // Small delay to ensure tab renders before we try to set state
          setTimeout(() => {
            const textarea = document.querySelector('textarea[placeholder*="Paste your VIP"]');
            if (textarea) {
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
              nativeInputValueSetter.call(textarea, json);
              textarea.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }, 100);
          toast.success('VIP loaded into Register tab');
        }} />
      )}
      {activeTab === 'browse' && <BrowseTab />}
      {activeTab === 'register' && <RegisterTab />}
      {activeTab === 'simulate' && <SimulateTab />}
      {activeTab === 'anchor' && <AnchorTab />}
    </div>
  );
}
